import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { trackEvent, LINKEDIN_PAGE_URL, trackFollowClick } from '../utils/analytics';
import { usePageMeta } from '../hooks/usePageMeta';

/* Contract persona values — labels shown to users map onto these. */
const PERSONA_OPTIONS: { value: string; label: string }[] = [
  { value: 'quality_manager', label: 'Quality Manager' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'other', label: 'Other' },
];

export default function FeedbackPage() {
  usePageMeta({ title: 'Feedback', description: 'Tell us what would make Food Safety Digest more useful in your work.' });
  const [message, setMessage] = useState('');
  const [persona, setPersona] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  /* Honeypot — real users never see or fill this field. */
  const [website, setWebsite] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const res = await fetch(`${API_BASE_URL}/api/public/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: message.trim(),
          persona: persona || undefined,
          name: name.trim() || undefined,
          email: email.trim() || undefined,
          website,
        }),
      });

      if (res.ok) {
        setSuccess(true);
        trackEvent('feedback_submitted', persona ? { persona } : undefined);
      } else {
        const data = await res.json().catch(() => null);
        setError(data?.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Could not reach the server. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <div className="subscribe-page">
        <div className="subscribe-card" style={{ textAlign: 'center' }}>
          <div className="subscribe-card__logo">FS</div>
          <h1 className="subscribe-card__title">Food Safety Digest</h1>
          <div className="subscribe-success">
            <div className="subscribe-success__icon">&#10003;</div>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--color-primary)', margin: '12px 0 8px' }}>
              Feedback received
            </h2>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', lineHeight: 1.6 }}>
              Thanks — your feedback goes straight into our roadmap review.
            </p>
            <Link to="/" className="subscribe-card__cta" style={{ marginTop: 20, display: 'inline-block' }}>
              Back to the dashboard
            </Link>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.82rem', marginTop: 18 }}>
              We post what we find twice a week.{' '}
              <a
                href={LINKEDIN_PAGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="admin-link"
                onClick={() => trackFollowClick('feedback_confirm')}
              >
                Follow Food Safety Digest on LinkedIn
              </a>
              .
            </p>

          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subscribe-page">
      <div className="subscribe-card">
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div className="subscribe-card__logo">FS</div>
          <h1 className="subscribe-card__title">Food Safety Digest</h1>
          <p className="subscribe-card__tagline">
            Tell us what would make this tool more useful for your work
          </p>
        </div>

        <form onSubmit={handleSubmit} className="subscribe-form">
          {/* Message */}
          <div className="subscribe-form__field">
            <label className="subscribe-form__label">Your feedback *</label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="A missing data source, a confusing screen, a feature idea…"
              required
              maxLength={2000}
              rows={6}
              className="subscribe-form__input"
              style={{ resize: 'vertical' }}
            />
          </div>

          {/* Role */}
          <div className="subscribe-form__field">
            <label className="subscribe-form__label">Your role <span style={{ fontWeight: 400, color: 'var(--color-text-tertiary)' }}>(optional)</span></label>
            <select
              value={persona}
              onChange={e => setPersona(e.target.value)}
              className="subscribe-form__input"
            >
              <option value="">Prefer not to say</option>
              {PERSONA_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Name */}
          <div className="subscribe-form__field">
            <label className="subscribe-form__label">Name <span style={{ fontWeight: 400, color: 'var(--color-text-tertiary)' }}>(optional)</span></label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Your name"
              className="subscribe-form__input"
            />
          </div>

          {/* Email */}
          <div className="subscribe-form__field">
            <label className="subscribe-form__label">
              Email <span style={{ fontWeight: 400, color: 'var(--color-text-tertiary)', textTransform: 'none' }}>Optional — only if you want a reply</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="subscribe-form__input"
            />
          </div>

          {/* Honeypot — hidden from real users, bots fill it in */}
          <input
            type="text"
            name="website"
            value={website}
            onChange={e => setWebsite(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
          />

          {error && (
            <div className="subscribe-form__error">{error}</div>
          )}

          <button type="submit" className="subscribe-card__cta" disabled={submitting}>
            {submitting ? 'Sending…' : 'Send feedback'}
          </button>

          <p className="subscribe-form__gdpr">
            Your message is stored so we can act on it. See our{' '}
            <Link to="/privacy">privacy policy</Link> for how we handle your data.
          </p>
        </form>
      </div>
    </div>
  );
}
